// src/App.js
import { Component, lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Box } from "@mui/system";
import { useAuth } from "@clerk/clerk-react";

import Navbar from "./components/Navbar/Navbar";
import { ReportingProvider } from "./components/Moderation/Reporting";
import { AccountModeration } from "./components/Moderation/AccountModeration";
import CookieConsentProvider from "./components/CookieConsent/CookieConsentProvider";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import InviteFriendsPromptGate from "./components/Profile/InviteFriendsPromptGate";
import { useAuthSync } from "./hooks/useAuthSync";
import {
  getStoredMapExplorationLocation,
  hasRequestedMapExplorationGeolocation,
  markMapExplorationGeolocationRequested,
  storeMapExplorationLocation,
} from "./utils/mapExploration";

const AuthPage = lazy(() => import("./components/Auth/AuthPage"));
const Feed = lazy(() => import("./components/Feed/Feed"));
const VideoDetail = lazy(() => import("./components/VideoItem/VideoDetail"));
const ChannelDetail = lazy(() => import("./components/StreamChannelItem/ChannelDetail"));
const MessagesPage = lazy(() => import("./components/Messages/MessagesPage"));
const NotificationsPage = lazy(() => import("./components/Notifications/NotificationsPage"));
const SearchFeed = lazy(() => import("./components/SearchFeed/SearchFeed"));
const MapExploration = lazy(() => import("./components/MapExploration/MapExploration"));
const QubiqRepublic = lazy(() => import("./components/QubiqRepublic/QubiqRepublic"));
const ProfilePage = lazy(() => import("./components/Profile/ProfilePage"));
const FriendGroupsPage = lazy(() => import("./components/Profile/FriendGroupsPage"));
const InviteFriendsPage = lazy(() => import("./components/Profile/InviteFriendsPage"));
const InviteReferralRedirectPage = lazy(() => import("./components/Profile/InviteReferralRedirectPage"));
const Ranking = lazy(() => import("./components/Ranking/Ranking"));
const TermsPage = lazy(() => import("./components/Legal/TermsPage"));
const LegalNoticePage = lazy(() => import("./components/Legal/LegalNoticePage"));
const PrivacyPolicyPage = lazy(() => import("./components/Legal/PrivacyPolicyPage"));
const CookiePolicyPage = lazy(() => import("./components/Legal/CookiePolicyPage"));
const ModerationRulesPage = lazy(() => import("./components/Legal/ModerationRulesPage"));
const Studio = lazy(() => import("./components/Studio/Studio"));
const StudioGpsPositionsPage = lazy(() => import("./components/Studio/StudioGpsPositionsPage"));
const MobileExploreCapturePage = lazy(() => import("./components/Studio/MobileExploreCapturePage"));
const LandingPage = lazy(() => import("./components/Landing/LandingPage"));
const StudioChannelSubscriptionRequestsPage = lazy(() => import("./components/Studio/StudioChannelSubscriptionRequestsPage"));
const TreasureHuntDetail = lazy(() => import("./components/TreasureHunt/TreasureHuntDetail"));
const ShareShortRedirectPage = lazy(() => import("./components/Share/ShareShortRedirectPage"));
const MobileAccountMenuPage = lazy(() => import("./components/Profile/MobileAccountMenuPage"));
const MobileAccountManagePage = lazy(() =>
  import("./components/Profile/MobileAccountMenuPage").then((module) => ({
    default: module.MobileAccountManagePage,
  }))
);

class RouteLoadBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: "100dvh",
            display: "grid",
            placeItems: "center",
            px: 3,
            color: "#fff",
            backgroundColor: "#0a0a0c",
            fontFamily: "var(--display-font)",
            fontSize: "1.35rem",
            textAlign: "center",
          }}
        >
          Impossible de charger cette page. Recharge-la pour réessayer.
        </Box>
      );
    }

    return this.props.children;
  }
}

const RouteLoadingScreen = () => (
  <Box
    sx={{
      minHeight: "100dvh",
      display: "grid",
      placeItems: "center",
      color: "#ffcf00",
      backgroundColor: "#0a0a0c",
      fontFamily: "var(--display-font)",
      fontSize: "1.5rem",
      letterSpacing: "var(--display-letter-spacing)",
    }}
  >
    Chargement...
  </Box>
);

const App = () => {
  // 🔗 synchronise Clerk ↔ backend (création user + merge counters)
  useAuthSync();

  return (
    <BrowserRouter>
      <CookieConsentProvider>
        <AccountModeration><ReportingProvider><AppShell /></ReportingProvider></AccountModeration>
      </CookieConsentProvider>
    </BrowserRouter>
  );
};

const AppShell = () => {
  const location = useLocation();
  const { isLoaded, isSignedIn } = useAuth();
  const isMobileExploreCaptureRoute = location.pathname === "/studio/capture-explore";
  const isMapRoute = location.pathname === "/map";
  const showLanding =
    location.pathname === "/" && (!isLoaded || !isSignedIn);

  useEffect(() => {
    if (showLanding) return;
    if (getStoredMapExplorationLocation()) return;
    if (hasRequestedMapExplorationGeolocation()) return;
    if (!navigator?.geolocation) return;

    let cancelled = false;
    markMapExplorationGeolocationRequested();

    const requestLocation = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (cancelled) return;
          storeMapExplorationLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            updatedAt: new Date().toISOString(),
          });
        },
        () => {},
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 120000,
        }
      );
    };

    if (!navigator?.permissions?.query) {
      requestLocation();
      return () => {
        cancelled = true;
      };
    }

    navigator.permissions
      .query({ name: "geolocation" })
      .then((permission) => {
        if (cancelled || permission.state === "denied") return;
        requestLocation();
      })
      .catch(() => {
        if (!cancelled) requestLocation();
      });

    return () => {
      cancelled = true;
    };
  }, [showLanding]);

  return (
    <>
      <ScrollToTop />
      <Box sx={{ backgroundColor: "#0a0a0c" }}>
        {!isMobileExploreCaptureRoute && !showLanding ? <Navbar /> : null}
        {!showLanding ? <InviteFriendsPromptGate /> : null}
        <Box
          sx={{
            pb: isMobileExploreCaptureRoute || isMapRoute || showLanding
              ? 0
              : { xs: "calc(60px + env(safe-area-inset-bottom))", sm: 0 },
          }}
        >
          <RouteLoadBoundary>
            <Suspense fallback={<RouteLoadingScreen />}>
              <Routes>
            <Route path="/" exact element={showLanding ? <LandingPage /> : <Feed />} />

            <Route path="/sign-in" element={<AuthPage initialTab="signin" />} />
            <Route path="/sign-up" element={<AuthPage initialTab="signup" />} />
            <Route path="/account" element={<MobileAccountMenuPage />} />
            <Route path="/account/manage/*" element={<MobileAccountManagePage />} />

            <Route path="/video/:id" element={<VideoDetail />} />
            <Route path="/channel/:id" element={<ChannelDetail />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/messages/:threadId" element={<MessagesPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/profile/:userId" element={<ProfilePage />} />
            <Route path="/friend-groups" element={<FriendGroupsPage />} />
            <Route path="/invite-friends" element={<InviteFriendsPage />} />
            <Route path="/search/:searchTerm" element={<SearchFeed />} />
            <Route path="/map" element={<MapExploration />} />
            <Route path="/i/:code" element={<InviteReferralRedirectPage />} />
            <Route path="/s/:code" element={<ShareShortRedirectPage />} />
            <Route path="/treasure-hunts/:id" element={<TreasureHuntDetail />} />
            <Route path="/qubiq-republic" element={<QubiqRepublic />} />
            <Route path="/ranking" element={<Ranking />} />
            <Route path="/studio" element={<Studio />} />
            <Route path="/studio/gps-points" element={<StudioGpsPositionsPage />} />
            <Route path="/studio/capture-explore" element={<MobileExploreCapturePage />} />
            <Route path="/studio/subscription-requests" element={<StudioChannelSubscriptionRequestsPage />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/mentions" element={<LegalNoticePage />} />
            <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/legal/cookies" element={<CookiePolicyPage />} />
            <Route path="/legal/moderation" element={<ModerationRulesPage />} />
              </Routes>
            </Suspense>
          </RouteLoadBoundary>
        </Box>
      </Box>
    </>
  );
};

export default App;
